import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TableRow, TableCell } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TrendingUp, TrendingDown, Building, Calendar, Globe, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { formatInvCurrency, formatInvDate, calcProfitLoss } from './investmentUtils';
import SimpleTooltip from '@/components/SimpleTooltip';

const TYPE_LABELS = {
  bank_balance: 'Bank Balance', fixed_deposit: 'Fixed Deposit', mutual_fund: 'Mutual Fund',
  treasury_bill: 'Treasury Bill', quoted_stocks: 'Quoted Stocks', government_bond: 'Government Bond',
  corporate_bond: 'Corporate Bond', private_equity: 'Private Equity', convertible: 'Convertible',
  safe: 'SAFE Investment', crypto: 'Cryptocurrency', real_estate: 'Real Estate (Property)', etf: 'ETF', gold: 'Gold'
};

const TYPE_CLR = {
  bank_balance: 'bg-blue-100 text-blue-800', fixed_deposit: 'bg-green-100 text-green-800',
  mutual_fund: 'bg-purple-100 text-purple-800', treasury_bill: 'bg-amber-100 text-amber-800',
  quoted_stocks: 'bg-emerald-100 text-emerald-800', government_bond: 'bg-indigo-100 text-indigo-800',
  corporate_bond: 'bg-slate-100 text-slate-800', private_equity: 'bg-rose-100 text-rose-800',
  convertible: 'bg-cyan-100 text-cyan-800', safe: 'bg-orange-100 text-orange-800',
  crypto: 'bg-yellow-100 text-yellow-800', real_estate: 'bg-teal-100 text-teal-800',
  etf: 'bg-violet-100 text-violet-800', gold: 'bg-yellow-100 text-yellow-800'
};

const InvestmentRow = ({ investment, groupByType, isReadOnly, onReadOnlyAction, onEdit, onDelete, deletingId }) => {
  const profitLoss = calcProfitLoss(investment);
  const currentValue = investment.current_value || investment.amount;
  return (
    <TableRow key={investment.investment_id} className="hover:bg-slate-50">
      <TableCell>
        <div className="space-y-1">
          <div className="font-medium text-slate-900"><span data-sensitive="asset-name">{investment.name}</span></div>
          {investment.ticker_symbol && <div className="text-sm font-mono text-slate-500"><span data-sensitive="asset-name">{investment.ticker_symbol}</span></div>}
          {investment.institution && <div className="flex items-center space-x-1 text-sm text-slate-500"><Building className="w-3 h-3" /><span data-sensitive="identifier">{investment.institution}</span></div>}
          <div className="flex items-center space-x-1 text-xs text-slate-400"><Calendar className="w-3 h-3" /><span>{formatInvDate(investment.purchase_date || investment.created_at)}</span></div>
        </div>
      </TableCell>
      {!groupByType && (
        <TableCell>
          <Badge variant="outline" className={TYPE_CLR[investment.type] || 'bg-gray-100 text-gray-800'}>{TYPE_LABELS[investment.type] || investment.type}</Badge>
        </TableCell>
      )}
      <TableCell className="text-right">
        <div className="font-mono font-medium text-slate-900"><span data-sensitive="monetary">{formatInvCurrency(investment.amount, investment.currency)}</span></div>
        <div className="text-sm text-slate-500">{formatInvDate(investment.purchase_date)}</div>
      </TableCell>
      <TableCell className="text-right">
        <div className="font-mono font-medium text-slate-900"><span data-sensitive="monetary">{formatInvCurrency(currentValue, investment.currency)}</span></div>
        {investment.currency !== 'USD' && <div className="flex items-center justify-end space-x-1 text-sm text-slate-500"><Globe className="w-3 h-3" /><span data-sensitive="identifier">{investment.currency}</span></div>}
      </TableCell>
      <TableCell className="text-right">
        <div className={`font-mono font-medium flex items-center justify-end space-x-1 ${profitLoss.isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
          {profitLoss.isPositive ? <TrendingUp className="w-4 w-4" /> : <TrendingDown className="w-4 h-4" />}
          <span data-sensitive="monetary">{profitLoss.isPositive ? '+' : ''}{formatInvCurrency(profitLoss.amount, investment.currency)}</span>
        </div>
        <div className={`text-sm ${profitLoss.isPositive ? 'text-emerald-600' : 'text-red-600'}`}><span data-sensitive="rate">{profitLoss.isPositive ? '+' : ''}{profitLoss.percentage.toFixed(2)}%</span></div>
      </TableCell>
      <TableCell>
        <div className="space-y-1 text-sm text-slate-600">
          {investment.interest_rate && <div>Rate: <span data-sensitive="rate">{investment.interest_rate}%</span></div>}
          {investment.maturity_date && <div className="flex items-center space-x-1"><Calendar className="w-3 h-3" /><span>Matures: {formatInvDate(investment.maturity_date)}</span></div>}
          {investment.fund_manager && <div>Manager: <span data-sensitive="identifier">{investment.fund_manager}</span></div>}
          {investment.risk_rating && <div>Risk: <span data-sensitive="identifier">{investment.risk_rating}</span></div>}
          {investment.country && investment.country !== investment.institution && <div className="flex items-center space-x-1"><Globe className="w-3 h-3" /><span data-sensitive="identifier">{investment.country}</span></div>}
        </div>
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <SimpleTooltip content="Edit or delete">
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="More actions for this investment" data-testid={`investment-menu-${investment.investment_id}`} className="h-8 w-8 p-0 hover:bg-slate-100"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
          </SimpleTooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid={`edit-investment-${investment.investment_id}`} onClick={() => isReadOnly ? onReadOnlyAction() : onEdit(investment)} disabled={isReadOnly} className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <Edit className="mr-2 h-4 w-4" />Edit
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`delete-investment-${investment.investment_id}`} onClick={() => isReadOnly ? onReadOnlyAction() : onDelete(investment.investment_id)} disabled={deletingId === investment.investment_id || isReadOnly} className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed">
              <Trash2 className="mr-2 h-4 w-4" />{deletingId === investment.investment_id ? 'Deleting...' : 'Delete'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

export default InvestmentRow;
